import { Query } from '@custom-types/Query';
import { Response } from '@custom-types/Response';
import apiFn from '@services/api-service';

export type Payload = {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  position?: string;
  customFields?: { id: string; value?: string | null; optionsValues?: string[] | null }[];
};

export enum FieldType {
  Text = 'text',
  Rating = 'rating',
  SingleSelect = 'single_select',
  MultiSelect = 'multi_select',
  DateTime = 'datetime',
}

enum Visibility {
  Profile = 'profile',
}

export type FieldOption = {
  id: string;
  name: string;
  order: number;
};

export type CustomFieldConfig = {
  id: string;
  name: string;
  type: FieldType;
  options?: FieldOption[];
  optionsValues?: string[] | null;
};

export type CustomField = CustomFieldConfig & {
  value?: string | null;
};

export type Lead = Payload & {
  id: string;
  companyId: string;
  capturedByPerson?: {
    id: string;
    externalId: string;
    firstName: string;
    lastName: string;
  } | null;
  customFields: CustomField[];
  updatedAt: string;
};

export type ListParams = {
  capturedByPerson?: string;
} & Query;

enum Include {
  CapturedByPerson = 'captured_by_person',
  CustomFields = 'custom_fields',
}
const DEFAULT_INCLUDES = [Include.CapturedByPerson, Include.CustomFields];
const DEFAULT_SORTER = 'updated_at';

const api = (eventId: string, companyId: string) =>
  apiFn(`/api/uapi/events/${eventId}/companies/${companyId}/leads`);

const apiCustomFieldsConfig = (eventId: string, companyId: string) =>
  apiFn(`/api/uapi/events/${eventId}/company_leads/${companyId}/fields`);

export const create = (eventId: string, companyId: string, payload: Payload) =>
  api(eventId, companyId)
    .post('', payload)
    .then((response: Response<Lead>) => response.data.data);

export const list = (
  eventId: string,
  companyId: string,
  query?: Partial<ListParams & { email: string }>
) => {
  const params = {
    limit: 10,
    include: DEFAULT_INCLUDES.join(','),
    sort: DEFAULT_SORTER,
    ...query,
  };

  return api(eventId, companyId)
    .get('', { params })
    .then((response: Response<Lead[]>) => response.data);
};

export const get = (eventId: string, companyId: string, leadId: string) =>
  api(eventId, companyId)
    .get(leadId, { params: { include: DEFAULT_INCLUDES.join(',') } })
    .then((response: Response<Lead>) => response.data.data);

export const update = (eventId: string, companyId: string, id: string, payload: Payload) =>
  api(eventId, companyId)
    .patch(id, payload)
    .then((response: Response<Lead>) => response.data.data);

export const getCustomFieldsConfig = (eventId: string, companyId: string) =>
  apiCustomFieldsConfig(eventId, companyId)
    .get('', {
      params: {
        visibility: Visibility.Profile,
      },
    })
    .then((response: Response<CustomFieldConfig[]>) => response.data.data);
